import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useOverlayContext } from 'seagull';
import { useIoContext, useStoreActions, useStoreStatic } from '~/contexts';
import { useSwapAuthToken } from '~/hooks/useSwapAuthToken';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { prevBusinessIdKey } from './useSwitchBusiness';
export const prevClientViewReturnUrlKey = 'prevClientViewReturnUrl';
export function useClientView() {
    const { t: commonTexts } = useLocaleKeys('common');
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { getState } = useStoreStatic();
    const { api } = useStoreActions();
    const { localStorage } = useIoContext();
    const history = useHistory();
    const swapAuthToken = useSwapAuthToken();
    const startClientView = useCallback(async (clientBusinessId, viewUserId)=>{
        const prevBusinessId = selectors.user.businessId(getState());
        const prevReturnUrl = "".concat(history.location.pathname).concat(history.location.search);
        startLoader('start-client-view');
        try {
            const authToken = await handlePromise(api.user.startClientView(clientBusinessId, viewUserId));
            if (authToken.data) {
                if (prevBusinessId) {
                    localStorage.setItem(prevBusinessIdKey, prevBusinessId);
                }
                localStorage.setItem(prevClientViewReturnUrlKey, prevReturnUrl);
                Sentry.addBreadcrumb({
                    type: 'startClientView',
                    data: {
                        clientBusinessId,
                        prevBusinessId,
                        prevReturnUrl
                    }
                });
                await swapAuthToken(authToken.data);
            } else {
                showErrorToast(commonTexts.toasts.failedToSwitchBusiness(), 'start-client-view-failure');
            }
        } finally{
            stopLoader('start-client-view');
        }
    }, [
        api,
        commonTexts,
        getState,
        history,
        localStorage,
        showErrorToast,
        startLoader,
        stopLoader,
        swapAuthToken
    ]);
    const exitClientView = useCallback(async ()=>{
        const prevBusinessId = localStorage.getItem(prevBusinessIdKey);
        if (!prevBusinessId) {
            showErrorToast(commonTexts.toasts.failedToSwitchBusiness(), 'exit-client-view-failure');
            return;
        }
        const prevReturnUrl = localStorage.getItem(prevClientViewReturnUrlKey);
        startLoader('exit-client-view');
        try {
            const authToken = await handlePromise(api.user.exitClientView(prevBusinessId));
            if (authToken.data) {
                localStorage.removeItem(prevBusinessIdKey);
                localStorage.removeItem(prevClientViewReturnUrlKey);
                Sentry.addBreadcrumb({
                    type: 'exitClientView',
                    data: {
                        prevReturnUrl
                    }
                });
                await swapAuthToken(authToken.data, prevReturnUrl ? {
                    route: prevReturnUrl
                } : undefined);
            } else {
                showErrorToast(commonTexts.toasts.failedToSwitchBusiness(), 'exit-client-view-failure');
            }
        } finally{
            stopLoader('exit-client-view');
        }
    }, [
        api,
        commonTexts,
        localStorage,
        showErrorToast,
        startLoader,
        stopLoader,
        swapAuthToken
    ]);
    return {
        startClientView,
        exitClientView
    };
}
